import { useCallback, useMemo } from "react"
import {
  Block,
  Button,
  HeadingXSmall,
  Icon,
  ParagraphMedium,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { setOnboardingStep, useDappDispatch } from "#/redux-state"
import { useOnboardingWallet } from "#/shared/hooks"
import { usePosthogSignUpEvent } from "#/shared/hooks/posthog-events"
import { OnboardingStep } from "#/shared/types"

import OnboardingModalContent from "./OnboardingModalContent"
import OnboardingModalLinkedAccount from "./OnboardingModalLinkedAccounts"

type OnboardingModalAccountNotFoundProps = {
  closeModal: () => void
}

export default function OnboardingModalAccountNotFound({
  closeModal,
}: OnboardingModalAccountNotFoundProps) {
  const { walletIcon, walletName, walletBalance } = useOnboardingWallet()
  const { emitSignUpStartedEvent } = usePosthogSignUpEvent()

  const dispatch = useDappDispatch()
  const [css, theme] = useStyletron()

  const createAccountHandler = useCallback(() => {
    emitSignUpStartedEvent()
    dispatch(setOnboardingStep(OnboardingStep.CREATE_ACCOUNT))
  }, [dispatch, emitSignUpStartedEvent])

  const walletAccounts = useMemo(
    () => [
      { artwork: walletIcon, title: walletName, description: walletBalance },
    ],
    [walletIcon, walletName, walletBalance],
  )

  return (
    <OnboardingModalContent
      header="Oops! No account found"
      footer={
        <Block
          className={css({
            width: "100%",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            gap: theme.sizing.scale300,
          })}
        >
          <Button width="100%" onClick={createAccountHandler}>
            Create account
          </Button>
          <Button width="100%" onClick={closeModal} kind="secondary">
            Cancel
          </Button>
        </Block>
      }
    >
      <Block
        className={css({
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          paddingTop: theme.sizing.scale1000,
        })}
      >
        {/* TODO: Replace it with corrected variation of AlertCircle icon */}
        <Icon size={64} viewBox="0 0 56 56" fill="none">
          <path
            d="M28.0004 53.5999C42.1389 53.5999 53.6004 42.1384 53.6004 27.9999C53.6004 13.8614 42.1389 2.3999 28.0004 2.3999C13.8619 2.3999 2.40039 13.8614 2.40039 27.9999C2.40039 42.1384 13.8619 53.5999 28.0004 53.5999Z"
            fill="black"
            stroke="#FC7F79"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M28.0004 17.7599V27.9999M28.0004 38.2399H28.026M53.6004 27.9999C53.6004 42.1384 42.1389 53.5999 28.0004 53.5999C13.8619 53.5999 2.40039 42.1384 2.40039 27.9999C2.40039 13.8614 13.8619 2.3999 28.0004 2.3999C42.1389 2.3999 53.6004 13.8614 53.6004 27.9999Z"
            fill="black"
            stroke="#FFD2CD"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Icon>
        <HeadingXSmall
          className={css({
            textAlign: "center",
            margin: `${theme.sizing.scale600} 0 ${theme.sizing.scale500}`,
          })}
        >
          It looks like you don&apos;t have an <br />
          account yet.
        </HeadingXSmall>
        <ParagraphMedium marginBottom={theme.sizing.scale1200}>
          Would you like to create one?
        </ParagraphMedium>
        <OnboardingModalLinkedAccount accounts={walletAccounts} />
      </Block>
    </OnboardingModalContent>
  )
}
